import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Globe, Package, DollarSign } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { mockData } from '../data/mockData';
import { Button } from '../components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Legend, PieChart, Pie, Cell, ComposedChart, Line, Area
} from 'recharts';
import RevenueWorldMap from '../components/RevenueWorldMap';

const COLORS = ['#0A192F', '#C5A059', '#059669', '#3B82F6', '#8B5CF6'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-primary text-white rounded-lg px-4 py-3 shadow-xl">
        <p className="text-sm font-medium mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color === '#0A192F' ? '#C5A059' : entry.color }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}M
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const RevenueDetailPage = ({ year }) => {
  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const revenueData = mockData.revenueBreakdown[year];
  const prevYearData = year > 2024 ? mockData.revenueBreakdown[year - 1] : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="revenue-detail-page">
      {/* Header */}
      <div className="bg-primary text-white px-8 py-6">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/finance')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'pt' ? 'Finanças' : 'Finance'}
            </Button>
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded hover:bg-white/20 transition-colors"
              data-testid="revenue-language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium uppercase">{language}</span>
            </button>
          </div>
          <h1 className="font-serif text-3xl font-bold">
            {language === 'pt' ? 'Análise de Receita' : 'Revenue Analysis'}
          </h1>
          <p className="text-white/70 mt-2">
            {language === 'pt' ? 'Decomposição por tipo, quantidade e geografia' : 'Breakdown by type, quantity and geography'}
          </p>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-8 py-8 space-y-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gold-50 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-gold-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{language === 'pt' ? 'Receita Total' : 'Total Revenue'}</p>
            <p className="font-serif text-4xl font-semibold text-primary">{mockData.finance.yearly[year].revenue}M</p>
            {prevYearData && (
              <div className="flex items-center gap-1 mt-2 text-emerald-600">
                <TrendingUp className="w-4 h-4" />
                <span className="text-sm font-medium">+{Math.round((mockData.finance.yearly[year].revenue / mockData.finance.yearly[year-1].revenue - 1) * 100)}% vs {year-1}</span>
              </div>
            )}
          </div>

          {revenueData.byType.map((type, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS[idx]}15` }}>
                  <Package className="w-6 h-6" style={{ color: COLORS[idx] }} />
                </div>
              </div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{type.type}</p>
              <p className="font-serif text-3xl font-semibold text-primary">{type.value}M</p>
              <p className="text-sm text-slate-400 mt-1">{type.percentage}% {language === 'pt' ? 'do total' : 'of total'}</p>
            </div>
          ))}
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-2 gap-6">
          {/* Monthly Revenue by Type */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-serif text-lg font-semibold text-primary mb-4">
              {language === 'pt' ? 'Receita Mensal por Tipo' : 'Monthly Revenue by Type'}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueData.monthlyByType}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="regular" stackId="a" fill="#0A192F" name="Regular" radius={[0, 0, 0, 0]} />
                <Bar dataKey="special" stackId="a" fill="#C5A059" name="Special" radius={[0, 0, 0, 0]} />
                <Bar dataKey="rejected" stackId="a" fill="#94A3B8" name={language === 'pt' ? 'Rejeitados' : 'Rejected'} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Revenue by Type Pie */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-serif text-lg font-semibold text-primary mb-4">
              {language === 'pt' ? 'Distribuição por Tipo' : 'Distribution by Type'}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={revenueData.byType}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  dataKey="value"
                  nameKey="type"
                  label={({ type, percentage }) => `${type}: ${percentage}%`}
                >
                  {revenueData.byType.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quantity by Type */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-serif text-lg font-semibold text-primary mb-4">
            {language === 'pt' ? 'Quantidade por Tipo (Carats)' : 'Quantity by Type (Carats)'}
          </h3>
          <div className="grid grid-cols-3 gap-6">
            {revenueData.quantityByType.map((item, idx) => (
              <div key={idx} className="text-center p-6 rounded-xl" style={{ backgroundColor: `${COLORS[idx]}10` }}>
                <p className="font-serif text-4xl font-semibold" style={{ color: COLORS[idx] }}>
                  {(item.carats / 1000).toFixed(0)}K
                </p>
                <p className="text-sm text-slate-600 mt-2">{item.type}</p>
                <p className="text-xs text-slate-400">{language === 'pt' ? 'carats' : 'carats'}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Geography */}
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-serif text-lg font-semibold text-primary mb-4">
              <Globe className="w-5 h-5 inline mr-2" />
              {language === 'pt' ? 'Receita por Geografia' : 'Revenue by Geography'}
            </h3>
            <RevenueWorldMap data={revenueData.byGeography} language={language} />
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-serif text-lg font-semibold text-primary mb-4">
              {language === 'pt' ? 'Quota de Mercado por Região' : 'Market Share by Region'}
            </h3>
            <div className="space-y-4">
              {revenueData.byGeography.map((geo, idx) => (
                <div key={idx}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700">{geo.region}</span>
                    <span className="text-sm font-semibold text-primary">{geo.value}M ({geo.percentage}%)</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-3">
                    <div 
                      className="h-3 rounded-full transition-all duration-500" 
                      style={{ width: `${geo.percentage}%`, backgroundColor: COLORS[idx % COLORS.length] }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Year Comparison */}
        {prevYearData && (
          <div className="bg-gradient-to-br from-primary to-navy-800 rounded-xl p-6 text-white">
            <h3 className="font-serif text-lg font-semibold mb-4">
              {language === 'pt' ? 'Comparativo com' : 'Comparison with'} {year - 1}
            </h3>
            <div className="grid grid-cols-3 gap-6">
              {revenueData.byType.map((current, idx) => {
                const prev = prevYearData.byType[idx];
                const change = Math.round((current.value / prev.value - 1) * 100);
                return (
                  <div key={idx} className="bg-white/10 rounded-lg p-4">
                    <p className="text-sm text-white/70 mb-2">{current.type}</p>
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-2xl font-semibold">{current.value}M</p>
                        <p className="text-xs text-white/50">{year}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-semibold ${change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {change >= 0 ? '+' : ''}{change}%
                        </p>
                        <p className="text-xs text-white/50">{prev.value}M ({year-1})</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenueDetailPage;
